import { fetchCampaignReplyStatsOverviewQuery } from 'api/queries';
import { EMAIL } from 'constants/messageMediums';
import {
  selectIsExportUrlFiltersCAFeatureFlagOn,
  selectIsSMSLinkShorteningFeatureFlagOn,
} from 'core/userContext';
import { UrlFilterErrorToast } from 'modules/messagingInsights/components/UrlFilterErrorToast';
import { LOCATION_AND_DEVICE_KEY } from 'modules/messagingInsights/featureFlags';
import { validateQueryParams } from 'modules/messagingInsights/hooks/useGetValidatedUrlParams';
import { QueryParamErrors } from 'modules/messagingInsights/types';
import { sharedUrl } from 'modules/messagingInsights/utils';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { addNegativeToast, removeToastKey } from 'modules/toastContainer';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useAllowPreview } from 'utils/hooks/useAllowPreview';
import { useFeatureFlag } from 'utils/hooks/useFeatureFlag';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { isEmpty } from '@iterable/utils';
import { getUrlParams } from 'utils/urlParams';
import { useFiltersContext, useTimeFiltersContext } from './context';
import { useUrlParamsContext } from './context/UrlParamsContext';
import {
  selectAnalyticsMetricsOptions,
  selectCampaignAnalyticsStatsForTimerange,
  selectIsCustomConversion,
  selectTemplateId,
} from './selectors';
import { CampaignRepliesOverviewResultsDefinition } from './types';
import { getCampaignAnalyticsParamValueOptions } from './utils';

export const useLocationAndDevice = () => {
  return useAllowPreview(LOCATION_AND_DEVICE_KEY);
};

export const useQueryFilterParamsCA = (selectedMediums: string[]) => {
  const dispatch = useDispatch();
  const exportUrlFiltersCAFFOn = useSelector(selectIsExportUrlFiltersCAFeatureFlagOn);
  const SMSLinkShorteningFFOn = useSelector(selectIsSMSLinkShorteningFeatureFlagOn);
  const isCustomConversion = useSelector(selectIsCustomConversion);
  const statsForTimeRange = useSelector(selectCampaignAnalyticsStatsForTimerange);
  const AnalyticsMetricsOptions = useSelector(selectAnalyticsMetricsOptions);
  const { highLevelMetrics, analyticsMetrics } = useMetricsContext();
  const rawQueryParams = useQueryParams();

  const paramValueOptions = getCampaignAnalyticsParamValueOptions(
    SMSLinkShorteningFFOn,
    isCustomConversion,
    statsForTimeRange,
    AnalyticsMetricsOptions,
  );

  const { validatedQueryParams, errors } = validateQueryParams(
    rawQueryParams,
    selectedMediums,
    paramValueOptions,
    highLevelMetrics,
    analyticsMetrics,
  );

  const invalid = errors.invalidParamList.length || !isEmpty(errors.invalidValsByParam);
  useEffect(() => {
    if (exportUrlFiltersCAFFOn && selectedMediums[0] && statsForTimeRange && invalid) {
      const key = 'urlFilterError';
      dispatch(removeToastKey(key));
      dispatch(
        addNegativeToast(<UrlFilterErrorToast errors={errors as QueryParamErrors} />, { key }),
      );
    }
  }, [selectedMediums, statsForTimeRange]);

  return validatedQueryParams;
};

export const useShareUrl = () => {
  const { campaignId, medium } = useUrlParamsContext();
  const { dateParams, timePreset, timezone } = useTimeFiltersContext();
  const { startTime, endTime } = dateParams || {};
  const { overviewMetrics, timeSeriesMetrics, segmentMetrics, segment, region, variantId } =
    useFiltersContext();

  const join = (arr: string[]) => arr?.join(',') || '';
  const queryParams: { [param: string]: string } = {
    startTime: startTime?.toISOString() || '',
    endTime: endTime?.toISOString() || '',
    timePreset: startTime ? '' : timePreset,
    timezone,
    campaignId: campaignId?.toString() || '',
    overviewMetrics: join(overviewMetrics),
    timeSeriesMetrics: join(timeSeriesMetrics),
    segmentMetrics: join(segmentMetrics),
    segment: segment?.name || '',
    region: medium === EMAIL ? region[0] : '',
    variantId: variantId !== 'default' ? variantId.toString() : '',
  };

  return sharedUrl(queryParams);
};

export const useWhatsAppGAFF = () => useFeatureFlag('WhatsAppMessageChannelGA', false);
export const useAnalyticsBotFiltering = () => useFeatureFlag('AnalyticsBotFiltering', false);

export const useReplyOverviewStats = () => {
  const { search } = useLocation();
  const { campaignId } = getUrlParams(search);
  const templateId = useSelector(selectTemplateId);
  const { dateParams } = useTimeFiltersContext();
  const { startTime, endTime } = dateParams || {};

  return useGraphql<{ campaignReplyStatsOverview: CampaignRepliesOverviewResultsDefinition }>(
    fetchCampaignReplyStatsOverviewQuery,
    {
      skip: !campaignId || !templateId || !startTime || !endTime,
      fetchPolicy: 'cache-and-network',
      variables: {
        campaignId: Number(campaignId),
        startTime: startTime?.toISOString(),
        endTime: endTime?.toISOString(),
        templateId: Number(templateId),
      },
    },
  );
};
